import { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { EmptyState, PageHeader, SectionCard } from '@/components/admin/page';
import { api, ApiError } from '../lib/api';
import { displayAuditAction, displayPersonName, formatDate } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { Pager } from '../lib/Pager';

const PAGE_SIZE = 20;

interface Activity {
  id: string;
  timestamp: string;
  actor: { id: string | null; displayName: string | null };
  verb: string;
  targetType: string | null;
  rawAction: string;
}

const VERB_KEY: Record<string, string> = {
  created: 'activity.verb.created',
  updated: 'activity.verb.updated',
  deleted: 'activity.verb.deleted',
  ran: 'activity.verb.ran',
  accessed: 'activity.verb.accessed',
};
const TARGET_KEY: Record<string, string> = {
  employee: 'activity.target.employee',
  automation: 'activity.target.automation',
  soul: 'activity.target.soul',
  invite: 'activity.target.invite',
  member: 'activity.target.member',
  conversation: 'activity.target.conversation',
  tool: 'activity.target.tool',
  'api-key': 'activity.target.apiKey',
  pairing: 'activity.target.pairing',
};

export function ActivityPage() {
  const { t } = useI18n();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [kind, setKind] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => { setPage(0); }, [kind]);
  const pageItems = activities.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const fmtTime = (iso: string): string => {
    const d = new Date(iso);
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1) return t('activity.time.justNow');
    if (diffMin < 60) return t('activity.time.minAgo', { n: diffMin });
    if (diffMin < 1440) return t('activity.time.hourAgo', { n: Math.floor(diffMin / 60) });
    return formatDate(iso);
  };

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (kind) params.set('kind', kind);
      const body = await api<{ activities: Activity[] }>(`/v1/activity-feed?${params}`);
      setActivities(body.activities);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => { void fetchFeed(); }, [fetchFeed]);

  return (
    <div>
      <PageHeader
        title={t('activity.title')}
        description={t('activity.desc')}
        actions={(
          <>
            <Select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">{t('activity.all')}</option>
              <option value="employee">{t('activity.filterEmployee')}</option>
              <option value="automation">{t('activity.filterAutomation')}</option>
              <option value="soul">{t('activity.filterSoul')}</option>
              <option value="invite">{t('activity.filterInvite')}</option>
              <option value="member">{t('activity.filterMember')}</option>
              <option value="conversation">{t('activity.filterConversation')}</option>
            </Select>
            <Button variant="outline" onClick={fetchFeed} disabled={loading}>
              <RefreshCw className={loading ? 'animate-spin' : ''} />
              {loading ? t('common.refreshing') : t('common.refresh')}
            </Button>
          </>
        )}
      />

      {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

      <SectionCard title={t('activity.section')} description={t('activity.sectionDesc')}>
        {activities.length === 0 && !loading && !error ? (
          <EmptyState>{t('activity.empty')}</EmptyState>
        ) : (
          <div className="divide-y divide-border rounded-xl border bg-card">
            {pageItems.map((a) => {
              const actor = displayPersonName(a.actor.displayName, a.actor.id) ?? t('common.system');
              const actorSuffix = a.actor.displayName ? t('activity.line.actorSuffix') : t('activity.line.systemSuffix');
              const verb = VERB_KEY[a.verb] ? t(VERB_KEY[a.verb]) : a.verb;
              const targetKey = TARGET_KEY[a.targetType ?? ''];
              const target = targetKey ? t(targetKey) : (a.targetType ?? t('activity.target.default'));
              return (
                <div key={a.id} className="flex gap-3 p-4">
                  <div className="mt-2 size-2 shrink-0 rounded-full bg-primary" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm">
                      <span className="font-semibold">{actor}</span>
                      <span className="text-muted-foreground">{actorSuffix} {t('activity.line.targetSuffix', { target })}</span>
                      <span className="text-primary">{verb}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>{fmtTime(a.timestamp)}</span>
                      <Badge variant="muted">{displayAuditAction(a.rawAction)}</Badge>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <Pager page={page} pageSize={PAGE_SIZE} total={activities.length} onChange={setPage} />
      </SectionCard>
    </div>
  );
}
